(() => {
  const runtime = window.AuremeStaffstartDefaultOverrides?.runtime;
  if (!runtime) return;

  const {
    createEmptyMarkedElements,
    findStaffProfileSummaryRow,
    getStaffDetailPath,
    getTrimmedText,
    isStaffDetailPath,
    isStaffstartPath,
    normalizePathname,
    patchOrder,
    registerPatch,
    syncAttributeMarkers,
  } = runtime;

  const STAFF_DETAIL_COORDINATE_TAB_TEXT = 'コーディネート';
  const BREADCRUMB_TOP_TEXT = 'TOP';
  const BREADCRUMB_ATTRIBUTE = 'data-aureme-staffstart-breadcrumb';
  const STAFF_DETAIL_NAME_ATTRIBUTE = 'data-aureme-staffstart-staff-detail-name';
  const STAFF_DETAIL_DESCRIPTION_ATTRIBUTE = 'data-aureme-staffstart-staff-detail-description';
  const STAFF_DETAIL_COORDINATE_TAB_ATTRIBUTE =
    'data-aureme-staffstart-staff-detail-coordinate-tab';
  const STAFF_DETAIL_LINK_SELECTOR = 'a[href*="/apps/staffstart/staffs/"]';

  /**
   * @typedef {object} StaffDetailTypographyElements
   * @property {Set<HTMLElement>} names - スタッフ名 marker 対象
   * @property {Set<HTMLElement>} descriptions - スタッフ紹介文 marker 対象
   */

  /**
   * スタッフ詳細用の空 marker 対象を都度返す。
   * @returns {StaffDetailTypographyElements}
   */
  function createEmptyStaffDetailTypographyElements() {
    return {
      names: createEmptyMarkedElements(),
      descriptions: createEmptyMarkedElements(),
    };
  }

  /**
   * app が出力したパンくずブロックを返す。
   * @param {HTMLElement} root - STAFF START アプリの描画ルート
   * @returns {Set<HTMLElement>}
   */
  function collectBreadcrumbBlocks(root) {
    const appFrame = root.firstElementChild;
    // STAFF START アプリは最上位 frame の先頭子要素として標準パンくずを描画する。
    // 先頭 link の文言と p の有無を併用し、同階層の別ブロックとの誤判定を避ける。
    const breadcrumb = appFrame?.firstElementChild;
    if (!(breadcrumb instanceof HTMLElement)) return new Set();

    const firstLink = breadcrumb.querySelector('a[href]');
    if (!(firstLink instanceof HTMLAnchorElement)) return new Set();

    const hasBreadcrumbLabel = getTrimmedText(firstLink) === BREADCRUMB_TOP_TEXT;
    const hasBreadcrumbParagraph = Boolean(breadcrumb.querySelector('p'));
    if (!hasBreadcrumbLabel || !hasBreadcrumbParagraph) return new Set();

    return new Set([breadcrumb]);
  }

  /**
   * パンくずの非表示 marker を反映する。
   * @param {HTMLElement} root - STAFF START アプリの描画ルート
   * @param {Set<HTMLElement>} elements - marker 対象
   */
  function syncBreadcrumbMarkers(root, elements) {
    syncAttributeMarkers(root, BREADCRUMB_ATTRIBUTE, elements);
  }

  /**
   * スタッフ詳細の表示補正用 marker 対象をまとめて返す。
   * @param {HTMLElement} root - STAFF START アプリの描画ルート
   * @returns {StaffDetailTypographyElements}
   */
  function collectStaffDetailTypographyElements(root) {
    const elements = createEmptyStaffDetailTypographyElements();
    const summaryRow = findStaffProfileSummaryRow(root);
    if (!summaryRow) return elements;

    const name = summaryRow.querySelector('h2');
    if (name instanceof HTMLElement) {
      elements.names.add(name);
    }

    const descriptionBlock = summaryRow.nextElementSibling;
    const description =
      descriptionBlock instanceof HTMLParagraphElement
        ? descriptionBlock
        : descriptionBlock?.querySelector('p');
    if (description instanceof HTMLElement && getTrimmedText(description)) {
      elements.descriptions.add(description);
    }

    return elements;
  }

  /**
   * スタッフ詳細の表示補正用 marker を反映する。
   * @param {HTMLElement} root - STAFF START アプリの描画ルート
   * @param {StaffDetailTypographyElements} elements - marker 対象
   */
  function syncStaffDetailTypographyMarkers(root, elements) {
    syncAttributeMarkers(root, STAFF_DETAIL_NAME_ATTRIBUTE, elements.names);
    syncAttributeMarkers(root, STAFF_DETAIL_DESCRIPTION_ATTRIBUTE, elements.descriptions);
  }

  /**
   * スタッフ詳細のコーディネートタブかどうかを返す。
   * @param {HTMLAnchorElement} link - 判定対象 link
   * @returns {boolean}
   */
  function isStaffDetailCoordinateTabLink(link) {
    if (getTrimmedText(link) !== STAFF_DETAIL_COORDINATE_TAB_TEXT) return false;

    try {
      const linkUrl = new URL(link.href, window.location.origin);
      return normalizePathname(linkUrl.pathname) === getStaffDetailPath();
    } catch {
      return false;
    }
  }

  /**
   * スタッフ詳細のコーディネートタブを一覧する。
   * @param {HTMLElement} root - STAFF START アプリの描画ルート
   * @returns {Set<HTMLElement>}
   */
  function collectStaffDetailCoordinateTabs(root) {
    const tabs = new Set();

    for (const link of root.querySelectorAll(STAFF_DETAIL_LINK_SELECTOR)) {
      if (!(link instanceof HTMLAnchorElement)) continue;
      if (!isStaffDetailCoordinateTabLink(link)) continue;

      tabs.add(link);
    }

    return tabs;
  }

  /**
   * スタッフ詳細のコーディネートタブ marker を同期する。
   * @param {HTMLElement} root - STAFF START アプリの描画ルート
   * @param {Set<HTMLElement>} elements - marker 対象
   */
  function syncStaffDetailCoordinateTabMarkers(root, elements) {
    syncAttributeMarkers(root, STAFF_DETAIL_COORDINATE_TAB_ATTRIBUTE, elements);
  }

  registerPatch({
    order: patchOrder.common.breadcrumb,
    isTargetRoute: isStaffstartPath,
    collect: collectBreadcrumbBlocks,
    createEmptyTargets: createEmptyMarkedElements,
    apply: syncBreadcrumbMarkers,
  });

  registerPatch({
    order: patchOrder.staffDetail.typography,
    isTargetRoute: isStaffDetailPath,
    collect: collectStaffDetailTypographyElements,
    createEmptyTargets: createEmptyStaffDetailTypographyElements,
    apply: syncStaffDetailTypographyMarkers,
  });

  registerPatch({
    order: patchOrder.staffDetail.coordinateTab,
    isTargetRoute: isStaffDetailPath,
    collect: collectStaffDetailCoordinateTabs,
    createEmptyTargets: createEmptyMarkedElements,
    apply: syncStaffDetailCoordinateTabMarkers,
  });
})();
